import React, { useState } from 'react';
import { CheckoutConfig, CheckoutField } from '../../../types';

interface FieldsTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
}

export const CheckoutStudioFieldsTab: React.FC<FieldsTabProps> = ({ config, onUpdate }) => {
  const fields = config.customFields || [];
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('text');
  const [newRequired, setNewRequired] = useState(false);

  const handleToggleEnable = (id: string) => {
    onUpdate({ customFields: fields.map(f => (f.id === id ? { ...f, isEnabled: !f.isEnabled } : f)) });
  };

  const handleToggleRequired = (id: string) => {
    onUpdate({ customFields: fields.map(f => (f.id === id ? { ...f, required: !f.required } : f)) });
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const copy = [...fields];
    [copy[index], copy[targetIndex]] = [copy[targetIndex], copy[index]];
    onUpdate({ customFields: copy });
  };

  const handleAddField = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    const newField: CheckoutField = {
      id: `f_${Date.now()}`,
      label: newLabel.trim(),
      placeholder: `Enter ${newLabel.trim().toLowerCase()}`,
      type: newType,
      required: newRequired,
      isEnabled: true,
    };
    onUpdate({ customFields: [...fields, newField] });
    setNewLabel('');
    setShowAddModal(false);
  };

  return (
    <div className="space-y-4 text-left">
      <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Customer Form Fields</p>
            <p className="text-[11px] text-slate-500">Add, reorder, or toggle inputs required from customers.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all"
          >
            + Add Field
          </button>
        </div>

        {showAddModal && (
          <form onSubmit={handleAddField} className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-indigo-200 dark:border-indigo-800/60 space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Field Label</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Tax ID / VAT Number"
                  className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Field Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="w-full text-xs font-semibold px-2 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                >
                  <option value="text">Single Line Text</option>
                  <option value="email">Email Address</option>
                  <option value="tel">Phone Number</option>
                  <option value="checkbox">Agreement Checkbox</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 font-semibold cursor-pointer">
                <input
                  type="checkbox"
                  checked={newRequired}
                  onChange={(e) => setNewRequired(e.target.checked)}
                  className="rounded text-indigo-600"
                />
                <span>Mandatory Required</span>
              </label>

              <div className="flex gap-1.5">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-800">
                  Cancel
                </button>
                <button type="submit" className="px-3 py-1 bg-indigo-600 text-white rounded text-xs font-bold">
                  Save Field
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-2">
          {fields.map((field, idx) => (
            <div
              key={field.id}
              className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 transition-all ${
                field.isEnabled
                  ? 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                  : 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 opacity-60'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-0.5">
                  <button type="button" disabled={idx === 0} onClick={() => handleMove(idx, 'up')} className="text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px]">▲</button>
                  <button type="button" disabled={idx === fields.length - 1} onClick={() => handleMove(idx, 'down')} className="text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px]">▼</button>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">{field.label}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">Key: {field.id} • Type: {field.type}</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => handleToggleRequired(field.id)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                    field.required ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                  }`}
                >
                  {field.required ? 'Mandatory' : 'Optional'}
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleEnable(field.id)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                    field.isEnabled ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {field.isEnabled ? 'Active' : 'Hidden'}
                </button>
                {idx >= 2 && (
                  <button type="button" onClick={() => onUpdate({ customFields: fields.filter(f => f.id !== field.id) })} className="p-1 text-slate-400 hover:text-rose-600 text-xs">
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
